'use client'
import React, { createContext, useContext, useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { usePolling } from '@/hooks/usePolling';
import { pingAlive, getBatchFinderServiceSettings } from '@/lib/api/batchfinder';
import { updateBatchFinderServiceLocation } from '@/lib/api/location';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { ErrorAlert } from '@/components/modals';
import { useInstacartAuth } from './InstacartAuthContext';
import { getLocal, setLocal } from '@/lib/storage';
import { useUserLocation } from './UserLocationContext';
import { usePathname } from 'next/navigation';

/**
 * Response type for the verification required response from pingAlive endpoint
 * with a 428 status code
 */
interface VerificationRequiredResponse {
  msg: 'Verification required';
  external_url: string;
}


// Mock batches for testing
export const mockBatches = [
  {
    id: "batch1",
    acceptLink: "batch1",
    earnings: 24.85,
    tip: 5.00,
    distance: 5.3,
    items: 15,
    units: 20,
    customers: 1,
    storeBrands: ["Albertsons"],
    storeAddresses: ["123 Main St"],
    metadata: {}
  },
  {
    id: "batch2",
    acceptLink: "batch2",
    earnings: 32.50,
    tip: 15.00,
    distance: 3.2,
    items: 28,
    units: 45,
    customers: 1,
    storeBrands: ["Publix"],
    storeAddresses: ["456 Oak Ave"],
    metadata: {}
  },
  {
    id: "batch3",
    acceptLink: "batch3",
    earnings: 18.75,
    tip: 2.50,
    distance: 2.8,
    items: 12,
    units: 18,
    customers: 1,
    storeBrands: ["Kroger"],
    storeAddresses: ["789 Pine Blvd"],
    metadata: {}
  },
  {
    id: "batch4",
    acceptLink: "batch4",
    earnings: 42.30,
    tip: 20.00,
    distance: 7.5,
    items: 35,
    units: 52,
    customers: 2,
    storeBrands: ["Whole Foods"],
    storeAddresses: ["101 Maple Dr"],
    metadata: {}
  },
  {
    id: "batch5",
    acceptLink: "batch5",
    earnings: 28.15,
    tip: 8.75,
    distance: 4.1,
    items: 22,
    units: 30,
    customers: 1,
    storeBrands: ["Safeway"],
    storeAddresses: ["202 Cedar St"],
    metadata: {}
  },
  {
    id: "batch6",
    acceptLink: "batch6",
    earnings: 36.90,
    tip: 12.50,
    distance: 6.3,
    items: 30,
    units: 40,
    customers: 2,
    storeBrands: ["Target"],
    storeAddresses: ["303 Elm Rd"],
    metadata: {}
  },
    {
      id: "batch7",
      acceptLink: "batch7",
      earnings: 42.30,
      tip: 20.00,
      distance: 7.5,
      items: 35,
      units: 52,
      customers: 2,
      storeBrands: ["Walmart"],
      storeAddresses: ["404 Pine Rd"],
      metadata: {}
    },
      {
        id: "batch8",
        acceptLink: "batch8",
        earnings: 42.30,
        tip: 20.00,
        distance: 7.5,
        items: 35,
        units: 52,
        customers: 2,
        storeBrands: ["Target"],
        storeAddresses: ["303 Elm Rd"],
        metadata: {}
      },
      {
        id: "batch9",
        acceptLink: "batch9",
        earnings: 42.30,
        tip: 20.00,
        distance: 7.5,
        items: 35,
        units: 52,
        customers: 2,
        storeBrands: ["Walmart"],
        storeAddresses: ["404 Pine Rd"],
        metadata: {}
      },
        {
          id: "batch10",
          acceptLink: "batch10",
          earnings: 42.30,
          tip: 20.00,
          distance: 7.5,
          items: 35,
          units: 52,
          customers: 2,
        }
];


interface AppContextProps {
  isBotRunning: boolean;
  toggleBotRunning: () => void;
  settings: any;
  locationSettings: {
    isRealLocation: boolean;
    displayName: string;
    latitude: number;
    longitude: number;
  } | null;
  setLocationSettings: React.Dispatch<React.SetStateAction<{
    isRealLocation: boolean;
    displayName: string;
    latitude: number;
    longitude: number;
  } | null>>;
  serviceStopReason: string | null;
  stopTimestamp: string | null;
  batches: any[];
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

const LOCAL_STORAGE_KEY = 'selectedLocation';

// Define the type for cached location
interface CachedLocation {
  name: string;
  latitude: number;
  longitude: number;
}

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.log('[AppProvider] Rendered');

  const { sessionId, accountId } = useAuth();
  const { isInstacartLinked } = useInstacartAuth();
  const { userCoords } = useUserLocation();
  const router = useRouter();
  const pathname = usePathname();
  const [isBotRunning, setIsBotRunning] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  const [locationSettings, internalSetLocationSettings] = useState<{
    isRealLocation: boolean;
    displayName: string;
    latitude: number;
    longitude: number;
  } | null>(null);
  
  const lastManualUpdateRef = useRef<number>(0);

  const setLocationSettings: React.Dispatch<React.SetStateAction<{
    isRealLocation: boolean;
    displayName: string;
    latitude: number;
    longitude: number;
  } | null>> = useCallback((action) => {
    lastManualUpdateRef.current = Date.now();
    internalSetLocationSettings(prev => {
      const next = typeof action === 'function' ? action(prev) : action;
      
      // Update local storage immediately for better UX
      if (next && next.displayName) {
        const locationData = {
          name: next.displayName,
          latitude: next.latitude,
          longitude: next.longitude
        };
        try {
          setLocal(LOCAL_STORAGE_KEY, locationData);
        } catch (error) {
          console.error('[AppProvider] Error caching location:', error);
        }
      }
      return next;
    });
  }, []);
  const [serviceStopReason, setServiceStopReason] = useState<string | null>(null);
  const [stopTimestamp, setStopTimestamp] = useState<string | null>(null);
  const [batches, setBatches] = useState<any[]>([]);
  const [showStopReasonAlert, setShowStopReasonAlert] = useState(false);

  // Memoize toggleBotRunning
  const toggleBotRunning = useCallback(() => {
    setIsBotRunning((prev) => !prev);
  }, []);

  // Polling for pingAlive every 30 seconds - only if Instacart is linked
  usePolling({
    key: 'ping-alive',
    fetchFn: async () => {
      const response = await pingAlive(sessionId!);
      
      if (response.statusCode === 400 && response.body === 'Not allowed to go online') {
        return response; // Exit early without setting error
      }
      if (response.statusCode === 400 && response.body === 'Missing account profiles') {
        // console.log('[ping-alive] Missing account profiles, redirecting to identity page');
        // Don't redirect to identity if on onboarding page
        if (pathname !== '/onboarding') {
          router.push('/identity');
        }
        return response; // Exit early without setting error
      }
      if (response.statusCode !== 200) {
        // console.error('Ping failed');
      }
      return response;
    },
    interval: 30000,
    enabled: !!sessionId && isInstacartLinked, // Only enable if both session exists AND Instacart is linked
    showToast: false,
  });

  // Polling for getBatchFinderServiceSettings - only if Instacart is linked
  usePolling({
    key: 'batch-finder-settings',
    fetchFn: async () => {
      const response = await getBatchFinderServiceSettings(sessionId!);
      // console.log('[batch-finder-settings] Response:', response);
      if (response.statusCode === 200) {
        const icSettings = response.body.step_settings.instacart;
        
        // Update settings
        setSettings((prev: any) => {
          // Only update if different to avoid unnecessary re-renders
          if (JSON.stringify(prev) !== JSON.stringify(response.body)) {
            return response.body;
          }
          return prev;
        });
        
        // Update location settings only if they changed
        const newLocationSettings = {
          isRealLocation: icSettings.location_settings?.is_real_location,
          displayName: icSettings.location_settings?.display_name,
          latitude: Number(icSettings.location_settings?.latitude),
          longitude: Number(icSettings.location_settings?.longitude),
        };
        
        // Add validation to prevent NaN coordinates from being processed
        const hasValidCoordinates = 
          !isNaN(newLocationSettings.latitude) && 
          !isNaN(newLocationSettings.longitude) && 
          newLocationSettings.displayName;

        const timeSinceLastManualUpdate = Date.now() - lastManualUpdateRef.current;
        const isManualUpdateRecent = timeSinceLastManualUpdate < 10000; // 10 seconds buffer
          
        if (!hasValidCoordinates) {
          console.error('[AppProvider] Invalid location data received:', newLocationSettings);
        } else if (!isManualUpdateRecent) {
          // Only update from server if no recent manual update
          
          // Cache location settings in localStorage
          const locationData = {
            name: newLocationSettings.displayName,
            latitude: newLocationSettings.latitude,
            longitude: newLocationSettings.longitude
          };
          
          try {
            setLocal(LOCAL_STORAGE_KEY, locationData);
          } catch (error) {
            console.error('[AppProvider] Error caching location:', error);
          }

          internalSetLocationSettings(prev => {
            if (!prev) {
              return newLocationSettings;
            }
            
            // Use more precise comparison for coordinates
            const coordinatesChanged = 
              !prev.latitude || 
              !prev.longitude ||
              Math.abs(prev.latitude - newLocationSettings.latitude) > 0.0000001 ||
              Math.abs(prev.longitude - newLocationSettings.longitude) > 0.0000001;
            
            // Only update if the values actually changed
            if (
              prev.isRealLocation !== newLocationSettings.isRealLocation ||
              prev.displayName !== newLocationSettings.displayName ||
              coordinatesChanged
            ) {
              return newLocationSettings;
            }
            return prev;
          });
        }
        
        // Update service stop reason only if it changed
        if (serviceStopReason !== icSettings.stop_reason) {
          setServiceStopReason(icSettings.stop_reason);
          setStopTimestamp(icSettings.stop_timestamp); // Update timestamp
        }
        
        // Update batches only if they changed
        setBatches(prev => {
          // Temporarily use mock batches instead of real ones
          // return mockBatches;
          
          // Original code (commented out):
          if (JSON.stringify(prev) !== JSON.stringify(icSettings.batches)) {
            return icSettings.batches;
          }
          return prev;
        });

        // Check if step_function_id exists and update isBotRunning state accordingly
        if (!icSettings.step_function_id && isBotRunning) {
          setIsBotRunning(false);
          
          // If service was stopped because a batch was accepted, redirect to batch page
          if (icSettings.stop_reason === 'Accepted batch') {
            toast.success('Batch accepted! Redirecting to batch details.');
            router.push('/batch');
          }
        } else if (icSettings.step_function_id && !isBotRunning) {
          setIsBotRunning(true);
        }

        // Update location if using real location and we have new coordinates
        if (icSettings.location_settings?.is_real_location && 
            icSettings.location_settings?.display_name === "Current Location" &&
            userCoords) {
          await updateBatchFinderServiceLocation(
            sessionId!,
            userCoords.latitude,
            userCoords.longitude,
            true,
            "Current Location"
          );
        }
      } else {
        // console.error('Failed to fetch settings');
        // console.error('Failed to fetch settings');
      }
      return response;
    },
    interval: isBotRunning ? 2000 : 30000,
    enabled: !!sessionId && isInstacartLinked,
    showToast: false,
  });

  // Clear bot state when Instacart becomes unlinked
  useEffect(() => {
    if (!isInstacartLinked && isBotRunning) {
      // console.log('[AppProvider] Instacart account unlinked, stopping Ping Alive Polling');
      setIsBotRunning(false);
    }
  }, [isInstacartLinked, isBotRunning]);

  // Show alert when service stop reason changes
  useEffect(() => {
    // Don't show alert for accepted batches or active batches
    const skipAlertReasons = ['Accepted batch', 'Active Batch', 'User stopped', 'User requested'];
    
    if (serviceStopReason && !skipAlertReasons.includes(serviceStopReason)) {
      // Check if stop timestamp is more than 3 hours old
      if (stopTimestamp) {
        const stopTime = new Date(stopTimestamp);
        const currentTime = new Date();
        const timeDifference = currentTime.getTime() - stopTime.getTime();
        const threeHoursInMs = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
        
        // Only show alert if stop timestamp is less than 3 hours old
        if (timeDifference < threeHoursInMs) {
          setShowStopReasonAlert(true);
        }
      } else {
        // If no timestamp, show alert (backward compatibility)
        setShowStopReasonAlert(true);
      }
    }
  }, [serviceStopReason, stopTimestamp]);

  // Update server when location settings change
  useEffect(() => {
    if (!sessionId || !locationSettings) return;

    const updateServerLocation = async () => {
      try {
        const response = await updateBatchFinderServiceLocation(
          sessionId,
          locationSettings.latitude,
          locationSettings.longitude,
          locationSettings.isRealLocation,
          locationSettings.displayName
        );

        if (response.statusCode !== 200) {
          console.error('Failed to update location on server:', response.body);
          toast.error('Failed to update location on server');
        }
      } catch (error) {
        console.error('Error updating location on server:', error);
        toast.error('Failed to update location on server');
      }
    };

    updateServerLocation();
  }, [sessionId, locationSettings]);

  // Replace the useEffect that initializes from cache with a useLayoutEffect
  // to ensure it happens synchronously before any rendering
  useLayoutEffect(() => {
    try {
      const cachedLocation = getLocal<CachedLocation | null>(LOCAL_STORAGE_KEY, null);
      if (cachedLocation && !locationSettings) {
        setLocationSettings({
          isRealLocation: cachedLocation.name === "Current Location",
          displayName: cachedLocation.name,
          latitude: cachedLocation.latitude,
          longitude: cachedLocation.longitude
        });
      }
    } catch (error) {
      // console.error('[AppProvider] Error loading cached location:', error);
    }
  }, []);

  useEffect(() => {
    console.log('[AppProvider] isBotRunning changed:', isBotRunning);
  }, [isBotRunning]);

  useEffect(() => {
    console.log('[AppProvider] locationSettings changed:', locationSettings);
  }, [locationSettings]);

  useEffect(() => {
    console.log('[AppProvider] batches changed:', batches);
  }, [batches]);

  useEffect(() => {
    console.log('[AppProvider] serviceStopReason changed:', serviceStopReason);
  }, [serviceStopReason]);

  useEffect(() => {
    console.log('[AppProvider] stopTimestamp changed:', stopTimestamp);
  }, [stopTimestamp]);

  useEffect(() => {
    console.log('[AppProvider] settings changed:', settings);
  }, [settings]);

  const contextValue = React.useMemo(() => ({
    isBotRunning,
    toggleBotRunning,
    settings,
    locationSettings,
    setLocationSettings,
    serviceStopReason,
    stopTimestamp,
    batches
  }), [
    isBotRunning,
    toggleBotRunning,
    settings,
    locationSettings,
    setLocationSettings,
    serviceStopReason,
    stopTimestamp,
    batches
  ]);

  // Before returning the provider
  // console.log('[AppProvider] Providing value:', contextValue);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
      {/* Service Stop Reason Alert */}
      <ErrorAlert
        isOpen={showStopReasonAlert}
        onOpenChange={setShowStopReasonAlert}
        title="Service Stopped"
        description="The batch finder service has been stopped for the following reason:"
        errorMessage={serviceStopReason || "Unknown reason"}
        actionText="OK"
        cancelText=""
        showSupportLink={!['User requested', 'Accepted batch', 'Active Batch', 'User stopped'].includes(serviceStopReason || '')}
        supportInfo={`Account ID: ${accountId || 'Not available'}\nPlease contact support with a screenshot of this error.`}
      />
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}; 